import type { BtlApplication } from 'api/btlApplicationForm/types';
import { btlApplicationFormApi } from 'api/btlApplicationForm';
import { individualFormSchema } from 'pages/BtlApplicationFormPage/Applicants/Individual/formSchema';
import { mapIndividualToFormValues } from 'pages/BtlApplicationFormPage/Applicants/Individual/mapIndividualToFormValues';
import { makeFormReadonly } from 'pages/BtlApplicationFormPage/makeFormReadonly';
import { useIntl } from 'react-intl';

interface UseOoCaseApplicantIndividualFormParams {
  caseData?: BtlApplication;
  individualId?: string | null;
  isCaseLoading: boolean;
}

export function useOoCaseApplicantIndividualForm({
  caseData,
  individualId,
  isCaseLoading,
}: UseOoCaseApplicantIndividualFormParams) {
  const { formatMessage } = useIntl();

  const individualData = caseData?.borrower.individuals.find(individual => individual.id === individualId);
  const isFirstIndividual = caseData?.borrower.individualOrder[0] === individualId;

  if (isCaseLoading || !caseData || !individualId || !individualData) {
    return { isLoading: true as const };
  }

  const schemaProps = {
    formatMessage,
    variant: 'oo' as const,
    isFormEditable: false,
    isDipAccepted: true,
    isDipAcceptedOrLater: true,
    showGrossIncome: false,
    hasCompanyApplicant: false,
    isFirstIndividual: Boolean(isFirstIndividual),
  };

  const formSchema = individualFormSchema(schemaProps);

  return {
    isLoading: false as const,
    formProps: {
      columns: makeFormReadonly(formSchema),
      isFormEditable: false,
      omitNil: true,
      name: 'oo_applicant_individual',
      request: async () => {
        const individualValues = mapIndividualToFormValues(individualData);

        try {
          const incomeValues = await btlApplicationFormApi.getIndividualIncomes(
            caseData.id,
            caseData.borrower.id,
            individualId,
          );
          return { ...individualValues, ...incomeValues };
        } catch {
          return individualValues;
        }
      },
    },
  };
}
